import React, { Component, Fragment, type ComponentProps, type KeyboardEvent } from 'react';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { AutoSelected } from 'lib/react';

// Data
import { Endpoint as EndpointT, isTextIntl, ParamAny, ParamArray, ParamObject, TextIntl } from './types';

// Components
import { DefinitionTooltip } from '@carbon/react';
import { CloudDownload, Asterisk, CaretDown, CaretRight } from '@carbon/icons-react';
import { H2, H3, P } from 'src/components/markdown';

// Styles
import cn from 'clsx';
import * as css from './Api.module.scss';

export type EndpointProps = {
    data: EndpointT;
    lang: string;
};
type Props = EndpointProps & { intl: IntlShape };

type State = {
    isInputsExpanded: boolean;
    isOutputsExpanded: boolean;
};
const getInitialState = (): State => ({
    isInputsExpanded: false,
    isOutputsExpanded: false,
});

class Endpoint$ extends Component<Props, State> {
    readonly state = getInitialState();

    private getText = (
        input: Maybe<TextIntl>,
        fallback: null | string = null,
        render?: (node: string) => ReactElement,
    ): ReactNode => {
        const resolved = input == null ? fallback : EndpointT.getText(input, this.props.lang);
        if (resolved == null) return null;
        return render ? render(resolved) : resolved;
    };

    private getTable(data: ParamObject | ParamArray, conf: { hasDefaults: boolean }) {
        const { formatMessage } = this.props.intl;

        const rows: ReactNode[] = [];
        const $row = (key: Maybe<string>, val: ParamAny, lvl: number): void => {
            let exampleString: null | string = null;
            if (val.example != null) exampleString = JSON.stringify(val.example).replace(/"/g, '');

            const typeClassName = css[`t_${val.type}`];
            const getTypeofClassName = (value: unknown): string => cn(css.coloredType, css[`t_${typeof value}`]);
            const $typeNode: ReactElement[] = [
                <div
                    key="type-name"
                    children={val.type}
                    className={cn(css.startCase, css.coloredType, typeClassName, val.null && css.nullable)}
                />,
            ];

            let typeColorClassname = '';
            if (isTextIntl(val.default)) {
                // NOOP
            } else if (val.default == null) {
                typeColorClassname = css.t_keyword;
            } else if (val.type === 'enum') {
                typeColorClassname = getTypeofClassName(val.default);
            } else {
                typeColorClassname = typeClassName;
            }

            //
            // Append type parameters to the description column
            //
            const descriptionText = [this.getText(val.description)];
            switch (val.type) {
                case 'number': {
                    const num = <span className={css.value} children="n" />;
                    const lte = <span className={css.symbol} children="<=" />;
                    const gte = <span className={css.symbol} children=">=" />;
                    const min = <span className={css.bound} children={val.min} />;
                    const max = <span className={css.bound} children={val.max} />;

                    let x: null | ReactElement = null;
                    const hasMin = val.min != null;
                    const hasMax = val.max != null;
                    if (hasMin && hasMax) {
                        x = (
                            <Fragment>
                                {min}
                                {lte}
                                {num}
                                {lte}
                                {max}
                            </Fragment>
                        );
                    } else if (hasMin) {
                        x = (
                            <Fragment>
                                {num}
                                {gte}
                                {min}
                            </Fragment>
                        );
                    } else if (hasMax) {
                        x = (
                            <Fragment>
                                {num}
                                {lte}
                                {max}
                            </Fragment>
                        );
                    }

                    if (x)
                        descriptionText.push(
                            <div key="range" className={css.cellTypeNumberRange}>
                                <FormattedMessage defaultMessage="Valid range: {range}" values={{ range: x }} />
                            </div>,
                        );
                    break;
                }

                case 'enum': {
                    descriptionText.push(
                        <span
                            key="enum"
                            className={css.enumWrapper}
                            children={formatMessage(
                                { defaultMessage: 'Possible values: {values}' },
                                {
                                    values: val.options.map(v => (
                                        <span children={v} className={getTypeofClassName(v)} />
                                    )),
                                },
                            )}
                        />,
                    );
                    break;
                }

                // no default
            }

            const cells: ReactNode[] = [
                // Key
                <td key="key" className={css.cellKey}>
                    {lvl > 0 ? (
                        <span style={{ opacity: 0.3, pointerEvents: 'none' }} children={`${' >'.repeat(lvl)}\xa0`} />
                    ) : null}

                    <AutoSelected render={p => <span {...p} children={key} />} />

                    {val.required ? (
                        <DefinitionTooltip
                            openOnHover
                            align="right"
                            children={<Asterisk size="16" />}
                            definition={formatMessage({ defaultMessage: 'Required' })}
                            className={css.requiredHint}
                        />
                    ) : null}
                </td>,

                // Type
                <td key="type_and_exaxmple" className={cn(css.cellType)}>
                    {$typeNode}
                    {exampleString ? (
                        <DefinitionTooltip
                            openOnHover
                            align="top"
                            children={exampleString}
                            className={css.example}
                            definition={formatMessage({ defaultMessage: 'Example value' })}
                        />
                    ) : null}
                </td>,

                // Default
                conf.hasDefaults ? (
                    <td
                        key="default"
                        className={cn(css.cellDefault, typeColorClassname)}
                        children={isTextIntl(val.default) ? this.getText(val.default) : JSON.stringify(val.default)}
                    />
                ) : null,

                // Description
                <td key="description" className={css.cellDescription} children={descriptionText} />,
            ].filter(Boolean);
            rows.push(
                <tr key={rows.length} className={cn(css.tableRow, val.required && css.required)} children={cells} />,
            );
        };
        const $obj = (d: ParamObject, lvl: number) => {
            Object.entries(d.properties).forEach(([entryKey, entryValue]) => {
                renderAny(entryKey, entryValue, lvl);
            });
        };
        const $arr = (d: ParamArray, lvl: number) => {
            renderAny('[*]', d.entry, lvl);
        };

        function renderAny(key: Maybe<string>, d: ParamAny, lvl: number) {
            const isObject = d.type === 'object';
            const isArray = d.type === 'array';

            if (isObject) {
                if (lvl >= 0) $row(key, d, lvl);
                $obj(d, lvl + 1);
            } else if (isArray) {
                $row(key, d, lvl);
                $arr(d, lvl + 1);
            } else if (lvl >= 0) $row(key, d, lvl);
        }

        renderAny(null, data, -1);

        return (
            <div className={css.typesTableWrapper}>
                <table className={css.typesTable}>
                    <thead>
                        <tr>
                            <th className={css.cellKey} children={formatMessage({ defaultMessage: 'Key' })} />
                            <th
                                className={css.cellType}
                                children={formatMessage({ defaultMessage: 'Type / Example' })}
                            />
                            {conf.hasDefaults && (
                                <th
                                    className={css.cellDefault}
                                    children={formatMessage({ defaultMessage: 'Default' })}
                                />
                            )}
                            <th
                                className={css.cellDescription}
                                children={formatMessage({ defaultMessage: 'Description' })}
                            />
                        </tr>
                    </thead>
                    <tbody children={rows} />
                </table>
            </div>
        );
    }
    private onKeyDown = (handler: AnyFunction) => {
        return (e: KeyboardEvent<HTMLElement>) => {
            if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handler();
            }
        };
    };

    private renderIntro() {
        const { title, description, url, methods } = this.props.data;

        return (
            <Fragment>
                <H2 className={css.title}>
                    <CloudDownload size={20} />
                    &nbsp;
                    {this.getText(title)}
                </H2>

                {this.getText(description, null, d => (
                    <P className={css.description} children={d} />
                ))}

                <div className={css.url}>
                    <div
                        className={css.methods}
                        children={methods.map(m => (
                            <span key={m} children={m} />
                        ))}
                    />
                    <AutoSelected render={p => <code className={css.path} children={url} {...p} />} />
                </div>
            </Fragment>
        );
    }

    private toggleInputs = () => this.setState(s => ({ isInputsExpanded: !s.isInputsExpanded }));
    private renderInputs() {
        const { input } = this.props.data;
        const { formatMessage } = this.props.intl;
        const isExpanded = this.state.isInputsExpanded || !input;

        let inputsTable: ReactNode = null;
        if (isExpanded) {
            inputsTable = (
                <div
                    className={css.inputs}
                    children={
                        input
                            ? this.getTable(input, { hasDefaults: true })
                            : formatMessage({ defaultMessage: 'No parameters are accepted in this endpoint.' })
                    }
                />
            );
        }

        const titleProps: ComponentProps<typeof H3> = { className: css.title };
        let expandIcon: ReactNode;

        if (input) {
            titleProps.tabIndex = 0;
            titleProps.style = { cursor: 'pointer' };
            titleProps.onClick = this.toggleInputs;
            titleProps.onKeyDown = this.onKeyDown(this.toggleInputs);

            expandIcon = isExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />;
        }

        return (
            <Fragment>
                <H3 {...titleProps}>
                    <Fragment>
                        {expandIcon}
                        {formatMessage({ defaultMessage: 'Request Parameters' })}
                    </Fragment>
                </H3>

                {inputsTable}
            </Fragment>
        );
    }

    private toggleOutputs = () => this.setState(s => ({ isOutputsExpanded: !s.isOutputsExpanded }));
    private renderOutputs() {
        const { formatMessage } = this.props.intl;
        const { output } = this.props.data;
        const { isOutputsExpanded } = this.state;

        let outputsTable: ReactNode = null;
        if (isOutputsExpanded) {
            outputsTable = <div className={css.outputs} children={this.getTable(output, { hasDefaults: false })} />;
        }

        return (
            <Fragment>
                <H3
                    className={css.title}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={this.toggleOutputs}
                    onKeyDown={this.onKeyDown(this.toggleOutputs)}
                >
                    {isOutputsExpanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                    {formatMessage({ defaultMessage: 'Response Format' })}
                </H3>
                {outputsTable}
            </Fragment>
        );
    }

    render() {
        return (
            <section className={css.endpointRoot}>
                {this.renderIntro()}
                {this.renderInputs()}
                {this.renderOutputs()}
            </section>
        );
    }
}
export const Endpoint = injectIntl(Endpoint$);

export type ApiProps = {
    data: EndpointProps['data'][];
    lang: string;
};
export function Api({ lang, data }: ApiProps) {
    return (
        <section
            className={css.apiRoot}
            children={data.map((end, ind) => (
                <Endpoint key={ind} data={end} lang={lang} />
            ))}
        />
    );
}
